import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, CheckCheck, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  EmptyState,
  LoadingState,
  ErrorState,
  PageHeader,
  Card,
  FilterBar,
  FilterChip,
} from '@/components/ui'
import { useToast } from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContentProposal {
  id: string
  articleId: string
  articleItemCode: string
  articleManufacturer: string | null
  articleGroupName: string | null
  proposedName: string | null
  proposedDescription: string | null
  originalName: string
  originalDescription: string | null
  categoryRule: string | null
  status: 'pending' | 'approved' | 'rejected'
  createdAt: string
}

interface ContentStats {
  raw: number
  proposed: number
  approved: number
  pendingProposals: number
}

interface GenerateResult {
  generated: number
  skipped: number
  errors: string[]
}

// ─── Proposal Card ────────────────────────────────────────────────────────────

function ProposalCard({
  proposal,
  onApprove,
  onReject,
  isPending,
}: {
  proposal: ContentProposal
  onApprove: () => void
  onReject: () => void
  isPending: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <Card>
      <Card.Body className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="font-mono text-xs text-text-secondary">{proposal.articleItemCode}</span>
            {proposal.articleGroupName && (
              <span className="ml-2 text-xs text-text-secondary">· {proposal.articleGroupName}</span>
            )}
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button variant="primary" size="xs" onClick={onApprove} disabled={isPending} title="Freigeben">
              <ThumbsUp size={12} />
            </Button>
            <Button variant="danger" size="xs" onClick={onReject} disabled={isPending} title="Ablehnen">
              <ThumbsDown size={12} />
            </Button>
          </div>
        </div>

        {/* Name comparison */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="rounded-lg bg-surface border border-border p-2.5">
            <p className="text-xs font-medium text-text-secondary mb-1">Original</p>
            <p className="text-text-primary line-clamp-2">{proposal.originalName}</p>
          </div>
          <div className="rounded-lg bg-brand-50 border border-brand-200 p-2.5">
            <p className="text-xs font-medium text-brand-600 mb-1">Vorschlag</p>
            <p className="text-text-primary font-medium line-clamp-2">{proposal.proposedName ?? '—'}</p>
          </div>
        </div>

        {/* Description (collapsible) */}
        {proposal.proposedDescription && (
          <div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              Beschreibungstext {expanded ? 'ausblenden' : 'anzeigen'}
            </button>
            {expanded && (
              <div className="mt-2 rounded-lg bg-brand-50 border border-brand-200 p-3 text-sm text-text-primary whitespace-pre-line">
                {proposal.proposedDescription}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function TextAdjustmentSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [groupFilter, setGroupFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 20

  const { data: stats } = useQuery<ContentStats>({
    queryKey: ['content-stats'],
    queryFn: () => apiClient.get('/articles/content/stats').then((r) => r.data),
    staleTime: 30_000,
  })

  const { data: proposals = [], isLoading, error } = useQuery<ContentProposal[]>({
    queryKey: ['content-proposals', 'pending'],
    queryFn: () => apiClient.get('/articles/content/proposals', { params: { status: 'pending' } }).then((r) => r.data),
    staleTime: 30_000,
  })

  // Distinct groups for filter
  const groups = [...new Set(proposals.map((p) => p.articleGroupName).filter(Boolean) as string[])].sort()

  const filtered = groupFilter === 'all'
    ? proposals
    : proposals.filter((p) => p.articleGroupName === groupFilter)

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const { mutate: generate, isPending: generating } = useMutation({
    mutationFn: () => apiClient.post('/articles/content/generate', { onlyRaw: true, overwrite: false }),
    onSuccess: (res) => {
      const d = res.data as GenerateResult
      toast({
        title: 'Textgenerierung abgeschlossen',
        description: `${d.generated} Vorschläge erstellt, ${d.skipped} übersprungen${d.errors.length > 0 ? `, ${d.errors.length} Fehler` : ''}`,
        tone: d.errors.length > 0 ? 'warning' : 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      void queryClient.invalidateQueries({ queryKey: ['content-stats'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: approveAll, isPending: approvingAll } = useMutation({
    mutationFn: () => apiClient.post('/articles/content/proposals/approve-all', {}),
    onSuccess: (res) => {
      const d = res.data as { approved: number }
      toast({ title: `${d.approved} Texte freigegeben`, tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      void queryClient.invalidateQueries({ queryKey: ['content-stats'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: approveOne, isPending: approvingOne } = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/content/proposals/${id}/approve`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['content-proposals'] })
      void queryClient.invalidateQueries({ queryKey: ['content-stats'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: rejectOne, isPending: rejectingOne } = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/content/proposals/${id}/reject`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['content-proposals'] }),
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const isBusy = approvingOne || rejectingOne || approvingAll

  if (isLoading) return <LoadingState label="Textvorschläge werden geladen…" />
  if (error) return <ErrorState title="Fehler" description={(error as Error).message} />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Automatische Textanpassung"
        description="KI-generierte Artikel-Bezeichnungen und Beschreibungstexte — Vorschau und Freigabe vor Übernahme in den Artikelstamm."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => generate()} disabled={generating}>
              <Sparkles size={14} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Wird generiert…' : 'Batch-Generierung starten'}
            </Button>
            {proposals.length > 0 && (
              <Button variant="primary" size="sm" onClick={() => approveAll()} disabled={approvingAll}>
                <CheckCheck size={14} />
                {approvingAll ? 'Wird freigegeben…' : `Alle freigeben (${proposals.length})`}
              </Button>
            )}
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Ohne Text (raw)', value: stats?.raw ?? 0, highlight: (stats?.raw ?? 0) > 0 },
          { label: 'Vorschläge ausstehend', value: stats?.pendingProposals ?? proposals.length, highlight: proposals.length > 0 },
          { label: 'Genehmigt', value: stats?.approved ?? 0, highlight: false },
        ].map(({ label, value, highlight }) => (
          <div
            key={label}
            className={`rounded-lg border p-3 ${highlight ? 'border-warning-300 bg-warning-50' : 'border-border bg-surface'}`}
          >
            <p className="text-xs text-text-secondary">{label}</p>
            <p className="text-2xl font-bold tabular-nums text-text-primary">{value}</p>
          </div>
        ))}
        <div className="rounded-lg border border-border bg-surface p-3">
          <p className="text-xs text-text-secondary">Seitenweise anzeigen</p>
          <p className="text-sm text-text-secondary mt-1">
            {page}/{Math.max(1, pageCount)} · {filtered.length} Vorschläge
          </p>
        </div>
      </div>

      {/* Group filter */}
      {groups.length > 1 && (
        <FilterBar>
          <FilterChip active={groupFilter === 'all'} onClick={() => { setGroupFilter('all'); setPage(1) }}>
            Alle Gruppen
          </FilterChip>
          {groups.map((g) => (
            <FilterChip
              key={g}
              active={groupFilter === g}
              onRemove={groupFilter === g ? () => { setGroupFilter('all'); setPage(1) } : undefined}
              onClick={() => { setGroupFilter(g); setPage(1) }}
            >
              {g}
            </FilterChip>
          ))}
        </FilterBar>
      )}

      {/* Proposals */}
      {paginated.length === 0 ? (
        <EmptyState
          title="Keine ausstehenden Vorschläge"
          description={
            (stats?.raw ?? 0) > 0
              ? `${stats!.raw} Artikel ohne Text — klicke „Batch-Generierung starten" um Vorschläge zu erzeugen.`
              : 'Alle Artikel haben genehmigte Texte oder es liegen keine Vorschläge vor.'
          }
        />
      ) : (
        <div className="space-y-3">
          {paginated.map((p) => (
            <ProposalCard
              key={p.id}
              proposal={p}
              onApprove={() => approveOne(p.id)}
              onReject={() => rejectOne(p.id)}
              isPending={isBusy}
            />
          ))}

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setPage((v) => Math.max(1, v - 1))} disabled={page === 1}>
                Zurück
              </Button>
              <span className="text-sm text-text-secondary">{page} / {pageCount}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage((v) => Math.min(pageCount, v + 1))} disabled={page >= pageCount}>
                Weiter
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
